import { Alert, type AlertRole } from '../alert/Alert';
import { Button } from '../button/Button';
import type { ButtonColour } from '../button/buttonClasses';

export type QueryErrorAlertVariant = 'error' | 'warning';

export interface QueryErrorAlertProps {
    /** What failed to load, as a sentence. */
    message: string;
    /** Renders a Try again button that calls this when pressed. */
    onRetry?: () => void;
    /** Holds the button inert, but still focusable, while a refetch is in flight. */
    isRetrying?: boolean;
    /** `error` (default) is announced assertively, `warning` politely. */
    variant?: QueryErrorAlertVariant;
    /** Outer spacing only, for example `mt-6`. */
    className?: string;
}

const ROLES: Record<QueryErrorAlertVariant, AlertRole> = { error: 'alert', warning: 'status' };
const RETRY_COLOURS: Record<QueryErrorAlertVariant, ButtonColour> = { error: 'danger', warning: 'warning' };
const ROW_CLASSES = 'flex w-full flex-wrap items-center gap-x-4 gap-y-2';
const MESSAGE_CLASSES = 'min-w-0 flex-[1_1_16rem]';
const RETRY_BUTTON_CLASSES = 'shrink-0 whitespace-nowrap';

export function QueryErrorAlert({ message, onRetry, isRetrying = false, variant = 'error', className }: QueryErrorAlertProps) {
    return (
        <Alert variant={variant} role={ROLES[variant]} className={className}>
            <div className={ROW_CLASSES}>
                <p className={MESSAGE_CLASSES}>{message}</p>
                {onRetry && (
                    <Button
                        type="button"
                        size="xs"
                        variant={RETRY_COLOURS[variant]}
                        appearance="outline"
                        className={RETRY_BUTTON_CLASSES}
                        ariaDisabled={isRetrying}
                        onClick={onRetry}
                    >
                        Try again
                    </Button>
                )}
            </div>
        </Alert>
    );
}
